import { useEffect, useMemo, useState } from "react";
import { adminApi, formatApiError } from "@/lib/api";
import { format, addDays, startOfWeek } from "date-fns";
import { Loader2, Plus, Trash2, Send, Users2, Ban, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function ScheduleManager() {
  const [tab, setTab] = useState("week"); // week | staff | timeoff
  return (
    <div data-testid="schedule-manager" className="space-y-6">
      <div className="flex gap-2 border-b border-[var(--trio-border)]">
        <SubTab id="week" active={tab} onClick={setTab} icon={ClipboardList} label="This Week" />
        <SubTab id="staff" active={tab} onClick={setTab} icon={Users2} label="Team" />
        <SubTab id="timeoff" active={tab} onClick={setTab} icon={Ban} label="Time-Off Requests" />
      </div>
      {tab === "week" && <WeekBuilder />}
      {tab === "staff" && <TeamManager />}
      {tab === "timeoff" && <TimeOffQueue />}
    </div>
  );
}

function SubTab({ id, active, onClick, icon: Icon, label }) {
  return (
    <button
      onClick={() => onClick(id)}
      data-testid={`schedule-subtab-${id}`}
      className={`inline-flex items-center gap-2 px-4 py-3 text-xs tracking-widest uppercase font-bold border-b-2 -mb-px ${
        active === id
          ? "border-[var(--trio-primary)] text-[var(--trio-primary)]"
          : "border-transparent text-[var(--trio-muted)] hover:text-[var(--trio-text)]"
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
}

// =========================================================
// WEEK BUILDER
// =========================================================
function WeekBuilder() {
  const [weekStart, setWeekStart] = useState(() =>
    format(startOfWeek(new Date(), { weekStartsOn: 0 }), "yyyy-MM-dd")
  );
  const [staff, setStaff] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [addDefaults, setAddDefaults] = useState({});
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    load();
  }, [weekStart]);

  async function load() {
    setLoading(true);
    try {
      const [st, sh] = await Promise.all([
        adminApi.get("/admin/staff"),
        adminApi.get(`/admin/shifts?week_start=${weekStart}`),
      ]);
      setStaff(st.data);
      setShifts(sh.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }

  function shiftWeek(d) {
    const dt = new Date(weekStart + "T00:00:00");
    dt.setDate(dt.getDate() + d * 7);
    setWeekStart(format(dt, "yyyy-MM-dd"));
  }

  const days = Array.from({ length: 7 }, (_, i) =>
    format(addDays(new Date(weekStart + "T00:00:00"), i), "yyyy-MM-dd")
  );

  const shiftsByDay = useMemo(() => {
    const m = {};
    for (const d of days) m[d] = [];
    for (const s of shifts) {
      if (m[s.date]) m[s.date].push(s);
    }
    for (const k in m) m[k].sort((a, b) => a.start_time.localeCompare(b.start_time));
    return m;
  }, [shifts, weekStart]);

  const anyUnpublished = shifts.some((s) => !s.published);
  const publishedCount = shifts.filter((s) => s.published).length;

  async function publishWeek() {
    setPublishing(true);
    try {
      const { data } = await adminApi.post(`/admin/shifts/publish?week_start=${weekStart}`);
      toast.success(`Published ${data.published} shift${data.published === 1 ? "" : "s"}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setPublishing(false);
    }
  }

  async function deleteShift(sid) {
    if (!confirm("Delete this shift?")) return;
    try {
      await adminApi.delete(`/admin/shifts/${sid}`);
      setShifts((p) => p.filter((s) => s.id !== sid));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => shiftWeek(-1)}
            className="px-3 py-1.5 text-xs tracking-widest uppercase border border-[var(--trio-border)] hover:border-[var(--trio-text)]"
          >
            ← Prev Week
          </button>
          <div className="text-sm font-medium min-w-[220px] text-center">
            Week of {format(new Date(weekStart + "T00:00:00"), "MMM d, yyyy")}
          </div>
          <button
            onClick={() => shiftWeek(1)}
            className="px-3 py-1.5 text-xs tracking-widest uppercase border border-[var(--trio-border)] hover:border-[var(--trio-text)]"
          >
            Next Week →
          </button>
        </div>
        <button
          onClick={publishWeek}
          disabled={publishing || !anyUnpublished}
          data-testid="publish-week"
          className="inline-flex items-center gap-2 bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white px-5 py-2 text-xs tracking-widest uppercase font-bold disabled:opacity-40"
        >
          {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {anyUnpublished ? "Publish Week" : `${publishedCount} shifts published`}
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[var(--trio-muted)]">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-3">
          {days.map((d, i) => (
            <div
              key={d}
              data-testid={`sched-day-${d}`}
              className="border border-[var(--trio-border)] bg-[var(--trio-surface)] p-3 min-h-[180px] flex flex-col"
            >
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--trio-primary)] font-bold">
                    {DAYS[i]}
                  </div>
                  <div className="text-sm font-medium">{format(new Date(d + "T00:00:00"), "MMM d")}</div>
                </div>
                <button
                  onClick={() => {
                    setAddDefaults({ date: d });
                    setAddOpen(true);
                  }}
                  data-testid={`sched-add-${d}`}
                  className="p-1 text-[var(--trio-muted)] hover:text-[var(--trio-primary)]"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <div className="space-y-2 flex-1">
                {shiftsByDay[d].length === 0 ? (
                  <div className="text-xs text-[var(--trio-muted)] italic">—</div>
                ) : (
                  shiftsByDay[d].map((s) => (
                    <div
                      key={s.id}
                      data-testid={`sched-shift-${s.id}`}
                      className={`text-xs border p-2 ${
                        s.published
                          ? "border-green-500/50 bg-green-500/5"
                          : "border-[var(--trio-border)] bg-[var(--trio-bg)]"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="font-bold truncate">{s.employee_name}</div>
                          <div className="text-[var(--trio-muted)]">
                            {s.start_time}–{s.end_time}
                          </div>
                          {s.role && <div className="text-[10px] text-[var(--trio-muted)]">{s.role}</div>}
                        </div>
                        <button
                          onClick={() => deleteShift(s.id)}
                          data-testid={`sched-delete-${s.id}`}
                          className="text-[var(--trio-muted)] hover:text-red-500 shrink-0"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <ShiftAddDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        defaults={addDefaults}
        staff={staff}
        onCreated={(sh) => setShifts((p) => [...p, sh])}
      />
    </div>
  );
}

function ShiftAddDialog({ open, onOpenChange, defaults, staff, onCreated }) {
  const [form, setForm] = useState({
    employee_id: "",
    date: "",
    start_time: "16:00",
    end_time: "23:00",
    role: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm((f) => ({ ...f, ...defaults, employee_id: staff[0]?.id || "" }));
    }
  }, [open, defaults, staff]);

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await adminApi.post("/admin/shifts", form);
      onCreated(data);
      toast.success("Shift added");
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="shift-add-dialog"
        className="bg-[var(--trio-bg)] border-[var(--trio-border)] rounded-none max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-display uppercase text-2xl tracking-tight">Add Shift</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs tracking-widest uppercase">Employee</Label>
            <select
              required
              value={form.employee_id}
              onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
              data-testid="shift-employee"
              className="mt-2 w-full bg-[var(--trio-bg)] border border-[var(--trio-border)] px-3 py-2 text-sm"
            >
              {staff.map((s) => (
                <option key={s.id} value={s.id}>
                  {`${s.name} (${s.position || "—"})`}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs tracking-widest uppercase">Date</Label>
              <Input
                type="date"
                required
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="shift-date"
                className="mt-2 rounded-none border-[var(--trio-border)] bg-[var(--trio-bg)]"
              />
            </div>
            <div>
              <Label className="text-xs tracking-widest uppercase">Role</Label>
              <Input
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                placeholder="Line Cook / Server…"
                data-testid="shift-role"
                className="mt-2 rounded-none border-[var(--trio-border)] bg-[var(--trio-bg)]"
              />
            </div>
            <div>
              <Label className="text-xs tracking-widest uppercase">Start</Label>
              <Input
                type="time"
                required
                value={form.start_time}
                onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                data-testid="shift-start"
                className="mt-2 rounded-none border-[var(--trio-border)] bg-[var(--trio-bg)]"
              />
            </div>
            <div>
              <Label className="text-xs tracking-widest uppercase">End</Label>
              <Input
                type="time"
                required
                value={form.end_time}
                onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                data-testid="shift-end"
                className="mt-2 rounded-none border-[var(--trio-border)] bg-[var(--trio-bg)]"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs tracking-widest uppercase">Notes</Label>
            <Input
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="shift-notes"
              className="mt-2 rounded-none border-[var(--trio-border)] bg-[var(--trio-bg)]"
            />
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="px-4 py-2 text-xs tracking-widest uppercase border border-[var(--trio-border)]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="shift-submit"
              className="inline-flex items-center gap-2 bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white px-5 py-2 text-xs tracking-widest uppercase font-bold disabled:opacity-60"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Add Shift
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// =========================================================
// TEAM MANAGER
// =========================================================
function TeamManager() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "", position: "", phone: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    try {
      const { data } = await adminApi.get("/admin/staff");
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await adminApi.post("/admin/staff", form);
      setRows((p) => [...(p || []), data]);
      setForm({ email: "", name: "", password: "", position: "", phone: "" });
      setOpen(false);
      toast.success(`Added ${data.name} — share their login: ${data.email}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }

  async function remove(id, name) {
    if (!confirm(`Remove ${name}? Their availability, shifts, and time-off will be deleted.`)) return;
    try {
      await adminApi.delete(`/admin/staff/${id}`);
      setRows((p) => p.filter((r) => r.id !== id));
      toast.success("Removed");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[var(--trio-muted)]">People on your team who can log into the Staff Portal.</p>
        <button
          onClick={() => setOpen(true)}
          data-testid="team-add-open"
          className="inline-flex items-center gap-2 bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white px-5 py-2 text-xs tracking-widest uppercase font-bold"
        >
          <Plus className="w-4 h-4" /> Add Employee
        </button>
      </div>
      {rows === null ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-[var(--trio-border)] p-10 text-center text-sm text-[var(--trio-muted)]">
          No team members yet.
        </div>
      ) : (
        <div className="border border-[var(--trio-border)] divide-y divide-[var(--trio-border)]">
          {rows.map((r) => (
            <div key={r.id} data-testid={`team-row-${r.id}`} className="p-3 flex items-center gap-3 bg-[var(--trio-bg)]">
              <div className="flex-1 min-w-0">
                <div className="font-medium">{r.name}</div>
                <div className="text-xs text-[var(--trio-muted)]">
                  {r.email} · {r.position || "—"} · {r.phone || "no phone"}
                </div>
              </div>
              <button
                onClick={() => remove(r.id, r.name)}
                data-testid={`team-delete-${r.id}`}
                className="p-2 text-[var(--trio-muted)] hover:text-red-500"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[var(--trio-bg)] border-[var(--trio-border)] rounded-none max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display uppercase text-2xl tracking-tight">Add Employee</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            {[
              ["name", "Full Name", "text", true],
              ["email", "Email", "email", true],
              ["password", "Starting Password", "text", true],
              ["position", "Position", "text", false],
              ["phone", "Phone", "text", false],
            ].map(([k, label, type, req]) => (
              <div key={k}>
                <Label className="text-xs tracking-widest uppercase">{label}</Label>
                <Input
                  type={type}
                  required={req}
                  value={form[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  data-testid={`team-field-${k}`}
                  className="mt-2 rounded-none border-[var(--trio-border)] bg-[var(--trio-bg)]"
                />
              </div>
            ))}
            <DialogFooter>
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 text-xs tracking-widest uppercase border border-[var(--trio-border)]">
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                data-testid="team-submit"
                className="inline-flex items-center gap-2 bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white px-5 py-2 text-xs tracking-widest uppercase font-bold disabled:opacity-60"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Add
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =========================================================
// TIME-OFF QUEUE
// =========================================================
function TimeOffQueue() {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    try {
      const { data } = await adminApi.get("/admin/time-off");
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  async function decide(id, status) {
    try {
      await adminApi.patch(`/admin/time-off/${id}`, { status });
      setRows((p) => p.map((r) => (r.id === id ? { ...r, status } : r)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  return (
    <div className="space-y-3">
      {rows === null ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : rows.length === 0 ? (
        <div className="text-sm text-[var(--trio-muted)] italic">No time-off requests yet.</div>
      ) : (
        rows.map((r) => (
          <div
            key={r.id}
            data-testid={`timeoff-admin-row-${r.id}`}
            className="border border-[var(--trio-border)] bg-[var(--trio-surface)] p-3 flex items-center gap-3"
          >
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold">{r.employee_name || r.employee_id}</div>
              <div className="text-xs text-[var(--trio-muted)]">
                {format(new Date(r.date + "T00:00:00"), "EEE, MMM d, yyyy")} {r.reason && `· ${r.reason}`}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {r.status !== "approved" && (
                <button
                  onClick={() => decide(r.id, "approved")}
                  data-testid={`timeoff-approve-${r.id}`}
                  className="px-3 py-1.5 text-[10px] tracking-widest uppercase font-bold border border-green-600 text-green-700 hover:bg-green-600 hover:text-white"
                >
                  Approve
                </button>
              )}
              {r.status !== "denied" && (
                <button
                  onClick={() => decide(r.id, "denied")}
                  data-testid={`timeoff-deny-${r.id}`}
                  className="px-3 py-1.5 text-[10px] tracking-widest uppercase font-bold border border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                >
                  Deny
                </button>
              )}
              <span className="px-2 py-1 text-[10px] tracking-widest uppercase border border-[var(--trio-border)]">
                {r.status}
              </span>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
